<i18n locale="en" lang="json">
{
  "profiel": "Profile",
  "logUit": "Sign out",
  "login": "Login",
  "customerService": "Customer service"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "profiel": "Profiel",
  "logUit": "Log uit",
  "login": "Inloggen",
  "customerService": "Klantenservice"
}
</i18n>

<template>
  <v-toolbar
    flat
    :class="currentTheme.dark ? 'bg-gray' : 'bg-white'"
    height="70"
    max-height="70"
  >
    <v-toolbar-title :style="isZekerArbo ? '' : 'margin-inline-start: 0px'">
      <base-router-link
        v-set-test-id="[componentName, 'logo']"
        style="width: 200px"
        :to="{
          name: RouteName.HomeIndex,
          query: $zvdzRoute.name === 'HomeIndex' ? $zvdzRoute.query : {},
        }"
      >
        <v-img
          :src="logo"
          :max-width="isZekerArbo ? '152px' : '350px'"
          :height="isZekerArbo ? '59px' : '70px'"
          contain
          alt="Logo"
        />
      </base-router-link>
    </v-toolbar-title>

    <v-spacer />

    <template v-if="xs">
      <v-btn
        v-if="isOrganisatieEPDinPA"
        v-set-test-id="[componentName, 'showInfo']"
        icon
        variant="text"
        color="on-surface"
        @click="onClickContact"
      >
        <font-awesome-icon :icon="faHeadset" size="2x" />
      </v-btn>
    </template>
    <template v-else>
      <base-button
        v-if="isOrganisatieEPDinPA"
        :icon="faHeadset"
        test-id="customer-service"
        variant="text"
        color="on-surface"
        @click="onClickContact"
      >
        {{ t('customerService') }}
      </base-button>
    </template>

    <v-btn
      v-if="loginLocation"
      v-set-test-id="[componentName, 'login']"
      :to="loginLocation"
      variant="text"
      rounded
      outlined
      offset-y
    >
      <font-awesome-icon
        :icon="faSignIn"
        size="lg"
        transform="shrink-5"
        class="mr-1"
      />
      {{ t('login') }}
    </v-btn>

    <div v-set-test-id="[componentName, 'scopes-menu']">
      <v-menu v-if="scopes && scopes.length > 1">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-set-test-id="[componentName, 'open-scopes-menu']"
            variant="text"
            class="hidden-xs"
            v-bind="menuProps"
          >
            <font-awesome-icon
              :icon="isSwitchingScope ? faSpinner : faBinoculars"
              :spin="isSwitchingScope"
              size="lg"
            />
            {{ '\xa0' }}
            {{ currentScopeName }}
            {{ '\xa0' }}
            <font-awesome-icon :icon="faChevronDown" size="xs" />
          </v-btn>
        </template>
        <v-list
          mandatory
          :selected="[currentScope]"
          color="primary"
          @update:selected="$emit('update:current-scope', $event)"
        >
          <v-list-item
            v-for="scope in scopes"
            :key="scope.value"
            v-set-test-id="[componentName, 'scopes-menu-item', scope.value]"
            :value="scope.value"
          >
            <v-list-item-title>{{ scope.title }} </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-menu v-if="scopes && scopes.length > 1">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-set-test-id="[componentName, 'open-scopes-menu-xs']"
            icon
            class="hidden-sm-and-up"
            v-bind="menuProps"
          >
            <font-awesome-icon
              :icon="isSwitchingScope ? faSpinner : faBinoculars"
              :spin="isSwitchingScope"
              size="lg"
            />
          </v-btn>
        </template>
        <v-list
          mandatory
          :selected="[currentScope]"
          color="primary"
          @update:selected="$emit('update:current-scope', $event)"
        >
          <v-list-item
            v-for="scope in scopes"
            :key="scope.value"
            v-set-test-id="[componentName, 'scopes-menu-item', scope.value]"
            :value="scope.value"
          >
            <v-list-item-title>{{ scope.title }} </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-menu v-if="profileLocation">
      <template #activator="{ props: menuProps }">
        <v-btn
          v-set-test-id="[componentName, 'open-user-menu']"
          variant="text"
          class="hidden-xs"
          v-bind="menuProps"
        >
          <font-awesome-icon :icon="faUserCircle" size="lg" />
          {{ '\xa0' }}{{ userName }}{{ '\xa0' }}
          <font-awesome-icon :icon="faChevronDown" size="xs" />
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-if="profileLocation"
          v-set-test-id="[componentName, profileLocation]"
          :to="profileLocation"
        >
          <v-list-item-title>{{ t('profiel') }}</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="loggedIn"
          v-set-test-id="[componentName, 'log-out']"
          @click="$emit('pressed-log-out')"
        >
          <v-list-item-title>{{ t('logUit') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-menu v-if="profileLocation || loggedIn">
      <template #activator="{ props: menuProps }">
        <v-btn
          v-set-test-id="[componentName, 'open-user-menu-xs']"
          icon
          class="hidden-sm-and-up"
          v-bind="menuProps"
        >
          <font-awesome-icon :icon="faUserCircle" size="lg" />
        </v-btn>
      </template>
      <v-list>
        <v-list-item
          v-if="profileLocation"
          v-set-test-id="[componentName, profileLocation]"
          :to="profileLocation"
        >
          <v-list-item-title>{{ t('profiel') }}</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="loggedIn"
          v-set-test-id="[componentName, 'log-out']"
          @click="$emit('pressed-log-out')"
        >
          <v-list-item-title>{{ t('logUit') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-divider v-if="showSideMenu" vertical class="ml-3" />

    <v-app-bar-nav-icon
      v-if="showSideMenu"
      v-set-test-id="[componentName, 'open-menu']"
      @click="$emit('pressed-menu')"
    />

    <base-dialog
      v-if="isOrganisatieEPDinPA"
      v-model:is-open="contactDialogIsOpen"
      :title="t('customerService')"
      width="500"
      test-id="customer-service"
    >
      <contact-info-zeker-arbo v-if="isZekerArbo" />
      <contact-data v-else :dienstverband-id="undefined" />
    </base-dialog>
  </v-toolbar>
</template>

<script lang="ts" setup>
import {
  faBinoculars,
  faChevronDown,
  faUserCircle,
  faSignIn,
  faSpinner,
  faHeadset,
} from '@fortawesome/pro-regular-svg-icons';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay, useTheme } from 'vuetify';
import type { ZvdZLocationRaw } from '@/plugins/router';
import logoZvdZO from '@/assets/logo-zvdzo.svg';
import logoZvdZODarkMode from '@/assets/logo-zvdzo-dark-mode.svg';
import logoZekerArbo from '@/assets/LogoZekerArbo_Tekst-BladGroen.svg';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';
import { isZekerArbo } from '@/common/netwerkbedrijven';
import useLoginData from '@/common/composition/useLoginData';
import ContactData from '@/areas/contact/common/ContactData.vue';
import ContactInfoZekerArbo from '@/areas/contact/common/ContactInfoZekerArbo.vue';

const theme = useTheme();
const currentTheme = computed(() => theme.current.value);
const componentName = 'BaseAppBar';

const { xs } = useDisplay();

type Props = ActionableComponentProps & {
  userName?: string;
  scopes?: { title: string; value: string }[];
  currentScope?: string;
  isSwitchingScope?: boolean;
  profileLocation?: ZvdZLocationRaw;
  loggedIn?: boolean;
  loginLocation?: ZvdZLocationRaw;
  showSideMenu?: boolean;
};
const props = withDefaults(defineProps<Props>(), { showSideMenu: true });
type Emits = {
  (e: 'pressed-log-out' | 'pressed-menu'): void;
  (e: 'update:current-scope', payload: (string | undefined)[]): void;
};
defineEmits<Emits>();

const { t } = useI18n();

const { autorisatieData } = useLoginData();
const contactDialogIsOpen = ref<boolean>(false);
const isOrganisatieEPDinPA = computed(() =>
  Boolean(autorisatieData.value?.isOrganisatieEPDinPA),
);

const darkModeMedia = window.matchMedia('(prefers-color-scheme: dark)');
const logoZvdZODarkOrLight = ref(
  darkModeMedia.matches ? logoZvdZODarkMode : logoZvdZO,
);
const logo = computed(() => {
  if (isZekerArbo.value) {
    return logoZekerArbo;
  }
  return logoZvdZODarkOrLight.value;
});

darkModeMedia.addEventListener('change', ({ matches }) => {
  if (!isZekerArbo.value) {
    logoZvdZODarkOrLight.value = matches ? logoZvdZODarkMode : logoZvdZO;
  }
});

const currentScopeName = computed(
  () => props.scopes?.find(({ value }) => value === props.currentScope)?.title,
);

const onClickContact = () => {
  contactDialogIsOpen.value = true;
};
</script>
